/**
 * @file useRadioEx.js
 * @description フォーム用ラジオボタンフィールドを簡単に生成するためのカスタムフック
 *
 * このフックは、Material-UIのRadioコンポーネントを使用して、
 * スタイリッシュで機能的なフォームラジオボタンフィールドを提供します。
 * データベーススキーマの性別・権限関連カラムから自動生成されます。
 *
 * @example
 * // 基本的な使用例
 * const [form, formProps] = useFormEx();
 * const { radio_sex, radio_auth } = useRadioEx(form, formProps);
 *
 * // コンポーネント内での使用
 * return (
 *   <form>
 *     {radio_sex({ label: "性別" })}
 *     {radio_auth({ label: "ユーザー権限" })}
 *   </form>
 * );
 *
 * @author MetroJS Team
 * @version 1.0.0
 * @since 2024-12-19
 */

"use client";

import logjs from "@metrojs/logjs";
const log = new logjs("useRadioEx");
const baseStyle = {
  cursor: "pointer",
}; // ベーススタイル

import Consts from "@common/config/consts";

/**
 * ラジオボタンフィールドを生成するためのカスタムフック
 * @param {Object} form - フォームデータオブジェクト
 * @param {Object} formProps - フォームプロパティオブジェクト
 * @param {Object} state - 状態オブジェクト
 * @param {Object} actions - アクションオブジェクト
 * @returns {Object} 各種ラジオボタンフィールド生成関数のオブジェクト
 */
const useRadioEx = (form = {}, formProps = {}, state = {}, actions = {}) => {
  const is_standard = true; // 標準のラジオボタンを使用するかどうか

  const radio_Sharyo_Handle = (params = {}, is_debug = false) => {
    if (is_debug) {
      log.debug(`radio_Sharyo_Handle form.Sharyo_Handle:${form?.Sharyo_Handle || ""} params:`, params);
    }

    const options = [
      {
        key: 1,
        value: "1",
        label: "右",
      },
      {
        key: 2,
        value: "2",
        label: "左",
      },
    ];

    return formProps.radio(
      "Sharyo_Handle",
      {
        ...params,
        label: "ハンドル",
        options: options,
        is_standard,
        style: { ...baseStyle, ...params.style },
      },
      is_debug
    );
  };
  const radio_PrepKind = (params = {}, is_debug = false) => {
    if (is_debug) {
      log.debug(`radio_PrepKind form.PrepKind:${form?.PrepKind || ""} params:`, params);
    }

    const options = [
      {
        key: Consts.GOODS.INHOUSE,
        value: String(Consts.GOODS.INHOUSE),
        label: "自社品",
      },
      {
        key: Consts.GOODS.OTHER,
        value: String(Consts.GOODS.OTHER),
        label: "他社品",
      },
    ];

    return formProps.radio(
      "PrepKind",
      {
        ...params,
        value: String(params.value) || "",
        label: "",
        options: options,
        is_standard,
        style: { ...baseStyle, ...params.style },
      },
      is_debug
    );
  };

  const radio_Zaiko = (params = {}, is_debug = false) => {
    if (is_debug) {
      log.debug(`radio_Zaiko form.Zaiko:${form?.Zaiko || ""} params:`, params);
    }

    const options = [
      {
        key: 1,
        value: String(Consts.HACCHU_FLG.KAKUTEI),
        label: "あり",
      },
      {
        key: 2,
        value: String(Consts.HACCHU_FLG.ZAIKONASHI),
        label: "なし",
      },
    ];

    return formProps.radio(
      "Zaiko",
      {
        ...params,
        label: "",
        options: options,
        is_standard,
        style: { ...baseStyle, ...params.style },
      },
      is_debug
    );
  };

  /**
   * 成功可否　1=成功 2=失敗ラジオボタンを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 成功可否　1=成功 2=失敗ラジオボタンのJSX要素
   */
  const radio_AuthorResult = (params = {}, is_debug = false) => {
    if (is_debug) {
      log.debug(`radio_AuthorResult form.AuthorResult:${form?.AuthorResult || ""} params:`, params);
      log.debug(`radio_AuthorResult state.initData.AuthorResult:`, state.initData?.AuthorResult);
    }

    const options = [
      {
        key: 1,
        value: 1,
        label: "オプション1",
      },
      {
        key: 2,
        value: 2,
        label: "オプション2",
      },
    ];

    return formProps.radio(
      "AuthorResult",
      {
        ...params,
        label: "成功可否　1=成功 2=失敗",
        options: options,
        is_standard,
        style: { ...baseStyle, ...params.style },
      },
      is_debug
    );
  };
  /**
   * 権限フラグラジオボタンを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 権限フラグラジオボタンのJSX要素
   */
  const radio_Author_flg = (params = {}, is_debug = false) => {
    if (is_debug) {
      log.debug(`radio_Author_flg form.Author_flg:${form?.Author_flg || ""} params:`, params);
      log.debug(`radio_Author_flg state.initData.Author_flg:`, state.initData?.Author_flg);
    }

    const options = [
      {
        key: 1,
        value: 1,
        label: "オプション1",
      },
      {
        key: 2,
        value: 2,
        label: "オプション2",
      },
    ];

    return formProps.radio(
      "Author_flg",
      {
        ...params,
        label: "権限フラグ",
        options: options,
        is_standard,
        style: { ...baseStyle, ...params.style },
      },
      is_debug
    );
  };

  return {
    radio_AuthorResult,
    radio_Author_flg,
    radio_PrepKind,
    radio_Sharyo_Handle,
    radio_Zaiko,
  };
};

export default useRadioEx;
